import { useState } from 'react';
import styled from 'styled-components';

import { colors } from 'constants/colors';

import {
    GetMicrostoreProductsQuery,
    MicrostoreColumnSetupFragmentFragment,
    MicrostoreColumnWithShelvesFragmentFragment,
    SetupInstructionType,
} from 'data/__generated__';

import { SETUP_INSTRUCTION_TYPE_LABELS } from 'pages/SiteSetups/constants/types';

// "Mettre les nouveaux produits" — same layout as the inventory section: column plan on the left,
// products list on the right. Driven by the setup snapshot (MicrostoreSetupLocation):
// target product (replacementProductId), quantity to add (stockToAdd) and DLC (expiryDates).
export const StoreProductsSection = ({
    column,
    columnSetup,
    products,
}: {
    column: MicrostoreColumnWithShelvesFragmentFragment;
    columnSetup: MicrostoreColumnSetupFragmentFragment;
    products: GetMicrostoreProductsQuery['getMicrostoreProducts'];
}) => {
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

    const setupByLocationId = new Map(
        (columnSetup.microstoreLocations ?? []).map((setupLocation) => [
            setupLocation.microstoreLocationId,
            setupLocation,
        ]),
    );
    const productsById = new Map(products.map((product) => [product._id, product]));

    const selectedTargetProductId = selectedLocationId
        ? (setupByLocationId.get(selectedLocationId)?.replacementProductId ?? null)
        : null;

    const setupLocationsToStore = (columnSetup.microstoreLocations ?? []).filter(
        ({ replacementProductId }) => !!replacementProductId,
    );
    const targetProductIds = [
        ...new Set(setupLocationsToStore.map(({ replacementProductId }) => replacementProductId as string)),
    ];
    const targetProducts = targetProductIds
        .map((productId) => productsById.get(productId) ?? { _id: productId, name: productId })
        .sort((productA, productB) => productA.name.localeCompare(productB.name));

    return (
        <Container>
            <Title>{SETUP_INSTRUCTION_TYPE_LABELS[SetupInstructionType.StoreProducts]}</Title>
            <Content>
                <ColumnDisplayContainer>
                    <Column>
                        {column.shelves.map((shelf) => (
                            <Shelf key={shelf._id} $numberOfLocationRow={shelf.locationTable.length}>
                                {shelf.locationTable.map((locationRow, rowIndex) => (
                                    <LocationRow key={rowIndex} $isFirstRow={rowIndex === 0}>
                                        {locationRow.map((location) => {
                                            const setupLocation = setupByLocationId.get(location._id);
                                            const targetProductId = setupLocation?.replacementProductId ?? null;
                                            const hasTarget = !!targetProductId;
                                            const isSelected =
                                                selectedLocationId === location._id ||
                                                (!!selectedTargetProductId &&
                                                    targetProductId === selectedTargetProductId);
                                            return (
                                                <Location
                                                    key={location._id}
                                                    onClick={() => setSelectedLocationId(location._id)}
                                                    $isSelected={isSelected}
                                                    $hasTarget={hasTarget}
                                                >
                                                    <LocationText>
                                                        {hasTarget ? setupLocation?.stockToAdd : ''}
                                                    </LocationText>
                                                </Location>
                                            );
                                        })}
                                    </LocationRow>
                                ))}
                            </Shelf>
                        ))}
                    </Column>
                    <LegendsContainer>
                        <Legend>
                            <LegendSymbol>
                                <Location $isSelected={false} $hasTarget>
                                    <LocationText>5</LocationText>
                                </Location>
                            </LegendSymbol>
                            <LegendText>Emplacement à remplir (quantité à ajouter)</LegendText>
                        </Legend>
                        <Legend>
                            <LegendSymbol>
                                <Location $isSelected $hasTarget>
                                    <LocationText>5</LocationText>
                                </Location>
                            </LegendSymbol>
                            <LegendText>Emplacement sélectionné</LegendText>
                        </Legend>
                    </LegendsContainer>
                </ColumnDisplayContainer>

                <ProductsContainer>
                    {targetProducts.length === 0 ? (
                        <EmptyState>Aucun produit à ajouter.</EmptyState>
                    ) : (
                        targetProducts.map((targetProduct) => {
                            const productSetupLocations = setupLocationsToStore.filter(
                                ({ replacementProductId }) => replacementProductId === targetProduct._id,
                            );
                            const isSelected = productSetupLocations.some(
                                ({ microstoreLocationId }) => microstoreLocationId === selectedLocationId,
                            );
                            return (
                                <ProductContainer key={targetProduct._id}>
                                    <ProductName
                                        onClick={() =>
                                            setSelectedLocationId(
                                                productSetupLocations[0]?.microstoreLocationId ?? null,
                                            )
                                        }
                                        $isSelected={isSelected}
                                    >
                                        {targetProduct.name}
                                    </ProductName>
                                    <LocationsContainer>
                                        {productSetupLocations.map((setupLocation, index) => (
                                            <LocationContainer key={setupLocation.microstoreLocationId}>
                                                <LocationName>Emplacement {index + 1}</LocationName>
                                                <QuantityContainer>
                                                    À ajouter : {setupLocation.stockToAdd}
                                                </QuantityContainer>
                                            </LocationContainer>
                                        ))}
                                    </LocationsContainer>
                                </ProductContainer>
                            );
                        })
                    )}
                </ProductsContainer>
            </Content>
        </Container>
    );
};

const Container = styled.div``;

const Title = styled.div`
    font-size: 18px;
    font-weight: bold;
    text-align: center;
`;

const Content = styled.div`
    display: flex;
    margin-top: 20px;
`;

const ColumnDisplayContainer = styled.div`
    width: 30%;
`;

const Column = styled.div`
    border-radius: 5px;
    padding: 5px;
    background-color: ${colors.pureWhite};
    width: 100%;
`;

const Shelf = styled.div<{ $numberOfLocationRow: number }>`
    width: 90%;
    margin: auto;
    transform: perspective(1200px) rotateX(45deg);
    /* Because of perspective, we need to readjust the margins */
    margin-top: ${({ $numberOfLocationRow }) => 15 - $numberOfLocationRow * 7}px;
    margin-bottom: ${({ $numberOfLocationRow }) => -$numberOfLocationRow * 5}px;
`;

const LocationRow = styled.div<{ $isFirstRow: boolean }>`
    display: flex;
    flex-direction: row;
    border: 1px solid ${colors.darkGrey};
    border-top: ${({ $isFirstRow }) => ($isFirstRow ? 1 : 0)}px solid ${colors.darkGrey};
    background-color: ${colors.pureWhite};
`;

const Location = styled.div<{ $isSelected: boolean; $hasTarget?: boolean }>`
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-basis: 0;
    flex-grow: 1;
    border: ${({ $isSelected, $hasTarget }) => {
        if ($isSelected) return `2px solid ${colors.red}`;
        if ($hasTarget) return `2px solid ${colors.green}`;
        return `1px solid ${colors.darkGrey}`;
    }};
    padding: 1px;
    margin: 2px;
    height: 35px;
`;

const LocationText = styled.span<{ $color?: string }>`
    font-size: 14px;
    color: ${({ $color }) => $color || colors.pureBlack};
`;

const LegendsContainer = styled.div`
    margin-top: 20px;
    padding: 10px;
`;
const Legend = styled.div`
    display: flex;
    align-items: center;
`;
const LegendSymbol = styled.div`
    width: 60px;
`;
const LegendText = styled.div`
    margin-left: 10px;
`;

const ProductsContainer = styled.div`
    flex-grow: 1;
    & > :not(:first-child) {
        border-top: 1px solid ${colors.lightGrey};
        margin-top: 5px;
    }
`;
const ProductContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;
const ProductName = styled.div<{ $isSelected: boolean }>`
    cursor: pointer;
    font-weight: bold;
    width: 50%;
    color: ${({ $isSelected }) => ($isSelected ? colors.red : colors.pureBlack)};
`;
const LocationsContainer = styled.div`
    margin-left: 10px;
    width: 50%;
`;
const LocationContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;
const LocationName = styled.div`
    font-style: italic;
`;
const QuantityContainer = styled.div`
    margin-left: 10px;
`;
const EmptyState = styled.div`
    text-align: center;
    color: ${colors.darkGrey};
`;
